// V2.4/V2.2/V2.1 (observability-completion) — "watch the watchers" Health page.
//
// Two surfaces on one page, conforming to observability-p2-mockup-v1.html:
//   1. Self-observability strip — telemetry-backend status, trace-link
//      coverage, span-drop rate, last-voice-metric age, + firing alert rules.
//      Catches the OpenObserve-down / voice-metrics-flatlined class in-app
//      (audit F11 + the 2026-09-04 incident).
//   2. Voice latency — per-agent TTFA waterfall (eou → transcription → ttft →
//      ttfb) with p50/p95, read from OpenObserve so it renders without an
//      external login (audit F4). Supervisor agents also get a per-expert
//      delegation-latency breakdown; clicking an expert re-scopes the panel.
//
// Platform-admin only (backend gates both endpoints via require_administrator).
// Styled on the platform thickGlass material + I² tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS, RADIUS } from "../../../theme/materials";
import { observabilityApi, agentApi } from "../../../api/client";
import {
  fmtDuration, fmtPct, fmtAgo, backendTiles, coverageState, metricAgeState,
  waterfallRows, expertCards, isSupervisor, WATERFALL_META,
} from "./healthFormat";

const PILL_COLOR = { ok: COLORS.green, warn: COLORS.orange, crit: COLORS.pink, dim: COLORS.textDim };
const RANGES = ["1h", "6h", "24h", "7d"];

const GLOSSARY = [
  ["EOU", "End-of-utterance — silence after the caller stops → turn is ready."],
  ["TTFT", "Time-to-first-token — delay until the LLM emits its first token."],
  ["TTFB", "Time-to-first-byte — delay until the TTS voice starts speaking."],
  ["TTFA", "Time-to-first-audio — total caller wait (EOU + transcribe + TTFT + TTFB)."],
  ["p50", "Median — half of turns are faster."],
  ["p95", "95th percentile — 19 of 20 turns are faster; the bad-case a caller feels."],
];

function Pill({ state, children }) {
  const c = PILL_COLOR[state] || COLORS.textDim;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
      padding: "2px 9px", borderRadius: RADIUS.pill, color: c, background: `${c}22`,
    }}>{children}</span>
  );
}

function Dot({ state }) {
  const c = PILL_COLOR[state] || COLORS.textDim;
  return <span style={{ width: 7, height: 7, borderRadius: "50%", background: c, boxShadow: `0 0 8px ${c}` }} />;
}

function glassCard(extra) {
  return { ...MATERIALS.thickGlass(), boxShadow: MATERIALS.spatialShadow(), ...extra };
}

// ── Self-observability strip ────────────────────────────────────────────

function SelfObservability({ health }) {
  const backends = backendTiles(health?.backends);
  const cov = health?.trace_link_coverage || {};
  const covPill = coverageState(cov.overall);
  const ageState = metricAgeState(health?.last_voice_metric_age_s);
  const alerts = health?.alerts || [];
  const rules = health?.alert_rules || [];

  return (
    <>
      <SectionH>Self-observability — telemetry pipeline health <Dim>(tenant-wide)</Dim></SectionH>
      <div style={glassCard({ padding: "4px 16px 16px" })}>
        {alerts.map((a, i) => (
          <div key={i} data-testid="obs-alert" style={{
            display: "flex", gap: 12, alignItems: "flex-start", background: `${COLORS.pink}22`,
            border: `1px solid ${COLORS.pink}66`, borderLeft: `3px solid ${COLORS.pink}`,
            borderRadius: RADIUS.deep, padding: "12px 14px", margin: "14px 0",
          }}>
            <div style={{ color: COLORS.pink, fontSize: 18, lineHeight: 1.2 }}>▲</div>
            <div>
              <b style={{ color: COLORS.pink }}>{a.message}</b>
              <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 3 }}>
                Rule <Mono>{a.rule}</Mono> · severity {a.severity}
                {a.detail ? <> · {a.detail}</> : null}
              </div>
            </div>
          </div>
        ))}
        {alerts.length === 0 && (
          <div data-testid="obs-allclear" style={{
            display: "flex", gap: 10, alignItems: "center", color: COLORS.green,
            fontSize: 12.5, margin: "14px 0 4px",
          }}><Dot state="ok" /> All telemetry backends healthy · coverage at SLO · voice metrics live.</div>
        )}

        <Grid cols={4}>
          {backends.map((t) => (
            <Tile key={t.key}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div style={{ color: COLORS.textDim, fontSize: 11, fontFamily: "var(--mono, monospace)", marginTop: 3 }}>{t.meta}</div>
                </div>
                <Dot state={t.state} />
              </div>
              <div style={{ marginTop: 10 }}><Pill state={t.state}>{t.label}</Pill></div>
            </Tile>
          ))}
        </Grid>

        <Grid cols={3} style={{ marginTop: 12 }}>
          <Tile>
            <Lab>Trace-link coverage <Pill state={covPill.state}>{covPill.label}</Pill></Lab>
            <Big>{fmtPct(cov.overall)}<Unit> %</Unit></Big>
            <Cap>turns whose trace is linked · SLO 100%</Cap>
          </Tile>
          <Tile>
            <Lab>Span drop rate (5m)</Lab>
            <Big>{health?.span_drop_rate == null ? "—" : fmtPct(health.span_drop_rate)}<Unit>{health?.span_drop_rate == null ? "" : " %"}</Unit></Big>
            <Cap>swallowed telemetry errors · alert &gt; 2%</Cap>
          </Tile>
          <Tile>
            <Lab>Last voice metric received</Lab>
            <Big style={{ color: ageState.state === "crit" ? COLORS.pink : undefined }}>
              {fmtAgo(health?.last_voice_metric_age_s)}<Unit> ago</Unit>
            </Big>
            <Cap>i2.voice.ttft · alert-on-absence &gt; 3m</Cap>
          </Tile>
        </Grid>
      </div>

      <SectionH>Alert rules <Dim>(alert on absence, not just errors)</Dim></SectionH>
      <div style={glassCard({ borderRadius: RADIUS.inner, overflow: "hidden" })}>
        {rules.map((r, i) => (
          <div key={i} data-testid="alert-rule" style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            padding: "9px 14px", borderBottom: i < rules.length - 1 ? `1px solid ${COLORS.border}` : "none",
          }}>
            <Mono>{r.cond}</Mono>
            <span style={{ color: COLORS.textMuted, fontSize: 11.5, textAlign: "right" }}>{r.desc}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Voice-latency panel ─────────────────────────────────────────────────

function VoiceLatencyPanel() {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);   // {id, name}
  const [supervisorRoot, setSupervisorRoot] = useState(null); // breadcrumb root when drilled
  const [range, setRange] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);

  useEffect(() => {
    agentApi.list().then((res) => {
      const list = (res?.data || res || []).map((a) => ({ id: a.id, name: a.name }));
      setAgents(list);
      if (list.length && !selected) setSelected(list[0]);
    }).catch(() => setAgents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    if (!selected?.id) return;
    setLoading(true);
    observabilityApi.getVoiceLatency(selected.id, range)
      .then((res) => setData(res?.data || res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selected, range]);

  useEffect(() => { load(); }, [load]);

  const supervisor = isSupervisor(data);
  const { rows, totalMs } = waterfallRows(data?.waterfall);
  const experts = supervisor ? expertCards(data?.experts) : [];

  const pickAgent = (a) => {
    setSupervisorRoot(null);
    setSelected(a);
    setDdOpen(false);
  };
  const drillExpert = (expertId) => {
    if (!selected) return;
    setSupervisorRoot(selected);
    const known = agents.find((a) => a.id === expertId);
    setSelected({ id: expertId, name: known?.name || expertId });
  };
  const backToRoot = () => {
    if (supervisorRoot) { setSelected(supervisorRoot); setSupervisorRoot(null); }
  };

  return (
    <>
      <SectionH>Voice latency — TTFA breakdown <Dim>(per-agent · i2.voice.*)</Dim></SectionH>
      <div style={glassCard({ padding: "18px 20px" })}>
        {/* breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.textDim, marginBottom: 14 }}>
          {supervisorRoot ? (
            <>
              <a data-testid="crumb-root" onClick={backToRoot} style={{ color: COLORS.accentText, cursor: "pointer" }}>{supervisorRoot.name}</a>
              <span style={{ opacity: 0.5 }}>›</span>
              <span style={{ color: COLORS.text }}>{selected?.name}</span>
            </>
          ) : (
            <span style={{ color: COLORS.text }}>{selected?.name || "Select an agent"}</span>
          )}
          {data ? <><span style={{ opacity: 0.5, marginLeft: 2 }}>·</span>
            <KindTag kind={data.agent_kind} /></> : null}
        </div>

        {/* selectors */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}>
          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 5 }}>
            <SelLab>Agent</SelLab>
            <span data-testid="agent-select" onClick={() => setDdOpen((v) => !v)} style={selBox(230)}>
              {selected?.name || "Select…"} <span style={{ color: COLORS.textMuted, fontSize: 11 }}>▾</span>
            </span>
            {ddOpen && (
              <div style={ddStyle()}>
                {agents.map((a) => (
                  <div key={a.id} data-testid="agent-option" onClick={() => pickAgent(a)} style={{
                    padding: "8px 10px", borderRadius: 9, fontSize: 13, cursor: "pointer",
                    background: a.id === selected?.id ? COLORS.accentSoft : "transparent",
                    color: a.id === selected?.id ? COLORS.accentText : COLORS.text,
                  }}>{a.name}</div>
                ))}
                {agents.length === 0 && <div style={{ padding: 8, color: COLORS.textDim, fontSize: 12 }}>No agents.</div>}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <SelLab>Range</SelLab>
            <select data-testid="range-select" value={range} onChange={(e) => setRange(e.target.value)} style={{
              ...selBox(120), appearance: "none",
              // The native menu inherits the control's own background/color in
              // most engines, and translucent glass gives it nothing to sit on
              // — so the control needs opaque tokens, not just the glass fill.
              background: COLORS.surface, color: COLORS.text,
            }}>
              {RANGES.map((r) => (
                <option key={r} value={r} style={{ background: COLORS.surface, color: COLORS.text }}>{`Last ${r}`}</option>
              ))}
            </select>
          </div>
        </div>

        {/* kind banner */}
        {data && (
          <div data-testid="kind-banner" style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            background: supervisor ? `${COLORS.purple}14` : `${COLORS.textDim}14`,
            border: `1px solid ${supervisor ? `${COLORS.purple}4d` : `${COLORS.textDim}33`}`,
            borderRadius: RADIUS.deep, padding: "10px 13px", margin: "16px 0 4px",
            fontSize: 12.5, color: COLORS.textMuted,
          }}>
            <KindTag kind={data.agent_kind} />
            <span>{supervisor
              ? <><b style={{ color: COLORS.text }}>Supervisor.</b> TTFT includes L1.5/L2 routing plus the delegated expert's generation; the per-expert breakdown below splits delegation latency by expert.</>
              : <><b style={{ color: COLORS.text }}>Regular agent.</b> Plain EOU → TTFT → TTFB pipeline — no delegation, so no routing/expert split.</>}
            </span>
          </div>
        )}

        {/* waterfall */}
        {loading && <div style={{ color: COLORS.textDim, padding: "18px 0" }}>Loading voice latency…</div>}
        {!loading && data && (
          <>
            <div data-testid="waterfall" style={{ margin: "16px 0 6px" }}>
              {rows.map((r) => (
                <div key={r.key} style={{ display: "grid", gridTemplateColumns: "200px 1fr 150px", alignItems: "center", gap: 12, margin: "7px 0" }}>
                  <div style={{ fontSize: 12.5 }}>
                    {r.label}<div style={{ color: COLORS.textDim, fontSize: 10.5 }}>{r.sub}</div>
                  </div>
                  <div style={{ position: "relative", height: 22, background: "rgba(255,255,255,0.05)", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, height: "100%", left: `${r.leftPct}%`, width: `${r.widthPct}%`, background: r.color }} />
                  </div>
                  <div style={{ fontFamily: "var(--mono, monospace)", fontVariantNumeric: "tabular-nums", textAlign: "right", fontSize: 12.5 }}>
                    {fmtDuration(r.ms)}<span style={{ color: COLORS.textDim }}> · p95 {fmtDuration(r.p95)}</span>
                  </div>
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 150px", alignItems: "center", gap: 12,
                borderTop: `1px dashed ${COLORS.border}`, marginTop: 8, paddingTop: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 12.5 }}>Est. time to first audio<div style={{ color: COLORS.textDim, fontSize: 10.5 }}>Σ median components</div></div>
                <div />
                <div data-testid="ttfa-total" style={{ fontFamily: "var(--mono, monospace)", textAlign: "right", color: COLORS.pink, fontWeight: 700 }}>{fmtDuration(totalMs)}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: 11, color: COLORS.textMuted }}>
              {WATERFALL_META.map((m) => (
                <span key={m.key}><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, marginRight: 5, background: m.color, verticalAlign: -1 }} />{m.label}</span>
              ))}
            </div>

            {/* per-expert breakdown */}
            {supervisor && experts.length > 0 && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "20px 0 8px" }}>
                  <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", color: COLORS.textMuted, fontWeight: 700 }}>Per-expert — click to drill into an expert's own latency</span>
                  <span style={{ color: COLORS.textDim, fontSize: 11 }}>{experts.length} expert{experts.length === 1 ? "" : "s"}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
                  {experts.map((e) => (
                    <div key={e.expertId} data-testid="expert-card" onClick={() => drillExpert(e.expertId)} style={{
                      background: MATERIALS.thinGlass().background, border: `1px solid ${COLORS.border}`,
                      borderRadius: RADIUS.deep, padding: "13px 15px", cursor: "pointer", position: "relative",
                    }}>
                      <span style={{ position: "absolute", top: 13, right: 14, color: COLORS.cyan, fontSize: 12, fontWeight: 600 }}>View detail →</span>
                      <div><KindTag kind="expert" /> <b style={{ marginLeft: 6 }}>{agents.find((a) => a.id === e.expertId)?.name || e.expertId}</b></div>
                      <div style={{ display: "flex", gap: 20, marginTop: 12 }}>
                        <KPI lab="TTFT p95" v={fmtDuration(e.p95)} color={COLORS.pink} />
                        <KPI lab="TTFT p50" v={fmtDuration(e.p50)} />
                        <KPI lab="turns" v={String(e.count)} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {!loading && !data && <div style={{ color: COLORS.textDim, padding: "18px 0" }}>No voice telemetry for this agent in the selected range.</div>}
      </div>
    </>
  );
}

// ── small primitives ────────────────────────────────────────────────────

function SectionH({ children }) {
  return <h2 style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".08em", color: COLORS.textMuted, margin: "30px 0 12px", fontWeight: 700 }}>{children}</h2>;
}
function Dim({ children }) { return <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, color: COLORS.textDim }}>{children}</span>; }
function Mono({ children }) { return <code style={{ fontFamily: "var(--mono, monospace)", fontSize: 12, color: COLORS.text }}>{children}</code>; }
function Tile({ children }) {
  return <div style={{ ...MATERIALS.thinGlass(), borderRadius: RADIUS.inner, padding: "14px 15px" }}>{children}</div>;
}
function Grid({ cols, style, children }) {
  return <div style={{ display: "grid", gap: 12, gridTemplateColumns: `repeat(${cols},1fr)`, ...style }}>{children}</div>;
}
function Lab({ children }) { return <div style={{ color: COLORS.textMuted, fontSize: 11.5, fontWeight: 600 }}>{children}</div>; }
function Big({ style, children }) { return <div style={{ fontFamily: "var(--mono, monospace)", fontVariantNumeric: "tabular-nums", fontSize: 24, fontWeight: 700, marginTop: 6, ...style }}>{children}</div>; }
function Unit({ children }) { return <span style={{ color: COLORS.textDim, fontSize: 12, fontWeight: 500 }}>{children}</span>; }
function Cap({ children }) { return <div style={{ color: COLORS.textDim, fontFamily: "var(--mono, monospace)", fontSize: 11, marginTop: 4 }}>{children}</div>; }
function SelLab({ children }) { return <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: COLORS.textMuted, fontWeight: 700 }}>{children}</span>; }
function KPI({ lab, v, color }) {
  return <div><div style={{ color: COLORS.textMuted, fontSize: 11 }}>{lab}</div><div style={{ fontFamily: "var(--mono, monospace)", fontSize: 18, fontWeight: 700, color }}>{v}</div></div>;
}
function KindTag({ kind }) {
  const map = {
    supervisor: { c: COLORS.purple, t: "Supervisor" },
    expert: { c: COLORS.cyan, t: "Expert" },
    regular: { c: COLORS.textMuted, t: "Regular" },
  };
  const m = map[kind] || map.regular;
  return <span data-testid="kind-tag" style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", padding: "2px 7px", borderRadius: RADIUS.pill, color: m.c, background: `${m.c}29` }}>{m.t}</span>;
}
function selBox(minWidth) {
  return {
    display: "inline-flex", alignItems: "center", gap: 10, background: MATERIALS.thinGlass().background,
    border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "8px 12px", fontSize: 13,
    fontWeight: 600, color: COLORS.text, cursor: "pointer", minWidth, justifyContent: "space-between",
  };
}
// A FUNCTION, not a const: `COLORS` is a single mutable object that
// applyTheme() rewrites in place, so a module-level object literal captures
// whichever theme was active at import and never updates on a theme switch.
// materials.js documents the same rule ("why functions, not constants").
// The background was a hardcoded rgba(23,27,51,.96) navy, which rendered a
// dark panel under near-black `COLORS.text` in light mode — dark on dark.
function ddStyle() {
  return {
    position: "absolute", zIndex: 5, top: "calc(100% + 6px)", left: 0, width: 300,
    background: COLORS.surface, backdropFilter: "blur(30px)", border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.deep, boxShadow: MATERIALS.spatialShadow(), padding: 6, maxHeight: 320, overflowY: "auto",
  };
}

// ── page ────────────────────────────────────────────────────────────────

export default function HealthPage() {
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);

  const loadHealth = useCallback(() => {
    observabilityApi.getHealth()
      .then((res) => { setHealth(res?.data || res); setErr(null); })
      .catch((e) => setErr(e?.message || "Failed to load health"));
  }, []);

  useEffect(() => {
    loadHealth();
    timer.current = setInterval(loadHealth, 30000);
    return () => timer.current && clearInterval(timer.current);
  }, [loadHealth]);

  return (
    <div data-testid="health-page">
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 10px", padding: "12px 15px", margin: "4px 0",
        ...MATERIALS.thinGlass(), boxShadow: MATERIALS.spatialShadow() }}>
        {GLOSSARY.map(([term, def]) => (
          <span key={term} style={{ fontSize: 12, color: COLORS.textMuted }}>
            <b style={{ color: COLORS.accentText, fontFamily: "var(--mono, monospace)", fontWeight: 700, fontSize: 11.5 }}>{term}</b> {def}
          </span>
        ))}
      </div>

      {err && <div data-testid="health-error" style={{ color: COLORS.pink, padding: "12px 0" }}>{err}</div>}
      {health && <SelfObservability health={health} />}
      <VoiceLatencyPanel />
    </div>
  );
}
